import { Component } from 'react';
import styles from './ErrorBoundary.module.css';

// Duplicated on purpose instead of imported from SiteConfigContext: this screen
// is the recovery path for a broken config, so it must not depend on the module
// that reads it.
const ADMIN_CONFIG_KEY = 'fgl_admin_config';

function hasStoredConfig() {
  try {
    return localStorage.getItem(ADMIN_CONFIG_KEY) !== null;
  } catch {
    return false;
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRecover = this.handleRecover.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] The app failed to render.', error, errorInfo);
  }

  handleRecover() {
    try {
      localStorage.removeItem(ADMIN_CONFIG_KEY);
    } catch {
      // Storage unavailable — reloading is still worth a try.
    }
    window.location.reload();
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const message = (error && error.message) || String(error);
    // Only blame the saved layout when there actually is one. Without this the
    // screen misdiagnoses an ordinary code bug, and its recovery button reloads
    // straight back into the same crash.
    const hasSavedConfig = hasStoredConfig();

    return (
      <div className={styles.screen} role="alert">
        <div className={styles.card}>
          <h1 className={styles.title}>
            {hasSavedConfig ? 'The saved layout could not be loaded' : 'Something went wrong'}
          </h1>
          <p className={styles.text}>
            {hasSavedConfig
              ? `The site layout saved in this browser is broken, so the page could not be rendered.
                 Discarding it brings back the layout that ships with the site. Only this browser is
                 affected — the published site is untouched.`
              : `The page could not be rendered. There is no saved layout in this browser to discard,
                 so this is a fault in the site itself. Reloading may help; if it does not, the
                 message below is what to report.`}
          </p>
          <pre className={styles.details}>{message}</pre>
          <button type="button" className={styles.button} onClick={this.handleRecover}>
            {hasSavedConfig ? 'Discard saved layout and reload' : 'Reload'}
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
